import { useMemo, useRef } from "react";
import * as THREE from "three";
import { useFrame } from "@react-three/fiber";
import { useSim } from "../../lib/store";

const N = 90;
const BLOCKS = [-30, -15, 0, 15, 30];

// Falling rubble chunks + dust puffs around cracked buildings (Mw >= 8).
export function Debris() {
  const meshRef = useRef();
  const dustRef = useRef();
  const dummy = useMemo(() => new THREE.Object3D(), []);
  const active = useRef(false);

  const chunks = useMemo(() => {
    const arr = [];
    for (let i = 0; i < N; i++) {
      const bx = BLOCKS[Math.floor(Math.random() * BLOCKS.length)] + (Math.random() - 0.5) * 1.6;
      const bz = BLOCKS[Math.floor(Math.random() * BLOCKS.length)] + (Math.random() - 0.5) * 1.6;
      arr.push({
        home: new THREE.Vector3(bx, 1 + Math.random() * 4, bz),
        pos: new THREE.Vector3(bx, 1 + Math.random() * 4, bz),
        vel: new THREE.Vector3(),
        rot: new THREE.Euler(Math.random() * 6, Math.random() * 6, Math.random() * 6),
        spin: new THREE.Vector3((Math.random() - 0.5) * 4, (Math.random() - 0.5) * 4, (Math.random() - 0.5) * 4),
        scale: 0.12 + Math.random() * 0.22,
        settled: false,
      });
    }
    return arr;
  }, []);

  const dust = useMemo(() => {
    const pos = new Float32Array(N * 3);
    const geom = new THREE.BufferGeometry();
    chunks.forEach((c, i) => {
      pos[i * 3] = c.home.x; pos[i * 3 + 1] = 0.3; pos[i * 3 + 2] = c.home.z;
    });
    geom.setAttribute("position", new THREE.BufferAttribute(pos, 3));
    return geom;
  }, [chunks]);

  useFrame((_, delta) => {
    const st = useSim.getState();
    const flood = window.__flood;
    const floodOn = flood && flood.active && flood.level > 0.2;
    const on = st.simulating && (st.magnitude >= 7.5 || floodOn);
    const mesh = meshRef.current;
    if (!mesh) return;

    if (on && !active.current) {
      // (re)start collapse
      chunks.forEach((c) => {
        c.pos.copy(c.home);
        c.vel.set((Math.random() - 0.5) * 1.5, Math.random() * 0.5, (Math.random() - 0.5) * 1.5);
        c.settled = false;
      });
    }
    active.current = on;
    mesh.visible = on;
    if (dustRef.current) dustRef.current.visible = on && !floodOn;
    if (!on) return;

    const dt = Math.min(delta, 0.05);
    const dpos = dust.attributes.position;
    for (let i = 0; i < N; i++) {
      const c = chunks[i];
      if (floodOn && c.pos.y < flood.level + 0.2) {
        // Hydro-drift: rubble floats to the surface and is carried by the current.
        c.vel.x += flood.dirX * 0.7 * dt;
        c.vel.z += flood.dirZ * 0.7 * dt;
        c.vel.x += Math.sin(_.clock.elapsedTime * 0.6 + i) * 0.3 * dt;
        c.pos.x += c.vel.x * dt;
        c.pos.z += c.vel.z * dt;
        c.pos.y += (flood.level + 0.12 - c.pos.y) * 0.05;
        c.rot.x += c.spin.x * 0.25 * dt;
        c.rot.y += c.spin.y * 0.25 * dt;
        if (c.pos.x > 72) c.pos.x = -72;
        if (c.pos.x < -72) c.pos.x = 72;
        if (c.pos.z > 72) c.pos.z = -72;
        if (c.pos.z < -72) c.pos.z = 72;
        c.settled = false;
      } else if (!c.settled) {
        c.vel.y -= 6 * dt;
        c.pos.addScaledVector(c.vel, dt);
        c.rot.x += c.spin.x * dt; c.rot.y += c.spin.y * dt; c.rot.z += c.spin.z * dt;
        if (c.pos.y <= c.scale / 2) {
          c.pos.y = c.scale / 2;
          c.settled = true;
        }
      }
      dummy.position.copy(c.pos);
      dummy.rotation.copy(c.rot);
      dummy.scale.setScalar(c.scale);
      dummy.updateMatrix();
      mesh.setMatrixAt(i, dummy.matrix);
      dpos.array[i * 3 + 1] = Math.min(2.2, dpos.array[i * 3 + 1] + dt * 0.4);
    }
    mesh.instanceMatrix.needsUpdate = true;
    dpos.needsUpdate = true;
  });

  return (
    <group>
      <instancedMesh ref={meshRef} args={[undefined, undefined, N]} visible={false} castShadow>
        <boxGeometry args={[1, 1, 1]} />
        <meshStandardMaterial color="#5b5048" roughness={0.95} metalness={0.05} />
      </instancedMesh>
      <points ref={dustRef} geometry={dust} visible={false}>
        <pointsMaterial color="#9c8f82" size={0.9} sizeAttenuation transparent opacity={0.28} depthWrite={false} />
      </points>
    </group>
  );
}
